import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class DataSharingService {
  private dataStore = new Map<string, any>();
  private dataSubjects = new Map<string, BehaviorSubject<any>>();

  /**
   * Set data for a specific key
   * @param key The string key to identify the data
   * @param data The data to store
   */
  setData(key: string, data: any): void {
    this.dataStore.set(key, data);
    
    // If there's an existing subject, emit the new value
    if (this.dataSubjects.has(key)) {
      this.dataSubjects.get(key)!.next(data);
    }
  }

  /**
   * Get data for a specific key
   * @param key The string key to retrieve data for
   * @returns The stored data or undefined if not found
   */
  getData(key: string): any {
    return this.dataStore.get(key);
  }

  /**
   * Get data as an observable for reactive updates
   * @param key The string key to observe
   * @returns Observable that emits the current value and future updates
   */
  getData$(key: string): Observable<any> {
    if (!this.dataSubjects.has(key)) {
      const subject = new BehaviorSubject<any>(this.dataStore.get(key));
      this.dataSubjects.set(key, subject);
    }
    return this.dataSubjects.get(key)!.asObservable();
  }

  /**
   * Check if data exists for a key
   * @param key The string key to check
   * @returns True if data exists, false otherwise
   */
  hasData(key: string): boolean {
    return this.dataStore.has(key);
  }

  /**
   * Remove data for a specific key
   * @param key The string key to remove
   */
  removeData(key: string): void {
    this.dataStore.delete(key);
    
    // Complete and remove the subject if it exists
    if (this.dataSubjects.has(key)) {
      this.dataSubjects.get(key)!.complete();
      this.dataSubjects.delete(key);
    }
  }

  /**
   * Clear module data when modal is closed
   */
  clearModuleData(): void {
    this.removeData('selected_module');
  }

  /**
   * Clear all stored data
   */
  clearAll(): void {
    this.dataStore.clear();
    
    // Complete and clear all subjects
    this.dataSubjects.forEach(subject => subject.complete());
    this.dataSubjects.clear();
  }

  /**
   * Get all available keys
   * @returns Array of all stored keys
   */
  getAllKeys(): string[] {
    return Array.from(this.dataStore.keys());
  }
}
